import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    TextField,
    Autocomplete,
    Button,
    Divider,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    FormControlLabel,
    Switch,
    CircularProgress
} from '@mui/material';
import {
    Add,
    Delete,
    Save,
    History,
    Search as SearchIcon,
    ArrowBack,
    Receipt
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { COLORS } from '../../settings/themeSettings';
import { fetchCircuit } from '../../data/slices/circuitSlice';
import { createFactureDossier } from '../../data/slices/facturesDossierSlice';
import { notify } from '../../settings/toastSetting';
import dayjs from 'dayjs';
import axiosInstance from '../../config/axiosConfig';

const UnifiedFacturationForm = () => {
    const { id, type: initialType } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Générer une référence automatique selon le mode de paiement
    const generateReference = (mode) => {
        const timestamp = Date.now();
        
        switch (mode) {
            case 'Espèces':
                return `ESP-${timestamp}`;
            case 'Mobile Money':
                return `MM-${timestamp}`;
            case 'Chèque':
            case 'Carte':
            case 'Virement':
            default:
                return ''; // L'utilisateur doit saisir manuellement
        }
    };

    // État du formulaire
    const [dossierType, setDossierType] = useState(initialType || 'circuit');
    const [selectedDossier, setSelectedDossier] = useState(null);
    const [dossiersList, setDossiersList] = useState([]);
    const [loadingDossiers, setLoadingDossiers] = useState(false);
    const [dossierSearchTerm, setDossierSearchTerm] = useState('');

    const [invoiceDate, setInvoiceDate] = useState(dayjs().format('YYYY-MM-DD'));
    const [numeroFacture, setNumeroFacture] = useState(''); // UX-13
    const [typeFacture, setTypeFacture] = useState('FC'); // Choix du type de facture
    const [currency, setCurrency] = useState('CFA');
    const [commentaire, setCommentaire] = useState('');

    // Infos Client Comptant en libre saisie
    const [adresseClientComptant, setAdresseClientComptant] = useState('');
    const [villeClientComptant, setVilleClientComptant] = useState('');
    const [paysClientComptant, setPaysClientComptant] = useState('');
    const [emailClientComptant, setEmailClientComptant] = useState('');
    const [telephoneClientComptant, setTelephoneClientComptant] = useState('');

    const [lastPrestationId, setLastPrestationId] = useState(null);
    const [lastReglementId, setLastReglementId] = useState(null);

    // Prestations (Bills)
    const [prestations, setPrestations] = useState([{ id: Date.now(), designation: '', nature: '', pu: 0, quantite: 1, montant: 0, currency: 'CFA' }]);

    // Règlements (Payments)
    const [reglements, setReglements] = useState([{ id: Date.now(), mode: 'Espèces', montant: 0, reference: generateReference('Espèces'), date: dayjs().format('YYYY-MM-DD'), currency: 'CFA' }]);

    // Recherche normalisée - charge tous les dossiers du type et laisse l'Autocomplete filtrer
    const searchDossiers = async (searchText = '') => {
        setLoadingDossiers(true);
        try {
            let url = '';
            if (dossierType === 'billet') {
                url = `/plane_tickets?type=billet`;
                if (searchText) url += `&query=${searchText}`; // Utilisation d'un paramètre plus générique si possible
            } else {
                url = `/circuits?type=${dossierType}`;
                // Si searchText est présent, on l'utilise pour filtrer (référence ou nom client côté serveur si supporté)
                if (searchText) url += `&search=${searchText}`; 
            }

            console.log('[DEBUG] 📥 Appel API searchDossiers:', url);
            const response = await axiosInstance.get(url);
            
            let finalData = [];
            if (Array.isArray(response.data)) {
                finalData = response.data;
            } else if (response.data && response.data['hydra:member']) {
                finalData = response.data['hydra:member'];
            } else if (response.data && response.data.data) {
                finalData = response.data.data;
            }

            // Si le serveur ne supporte pas le filtrage multi-critères, on peut aussi 
            // charger une liste plus large et laisser l'Autocomplete faire un filtrage local 
            // en enlevant : filterOptions={(x) => x}
            console.log(`✅ ${finalData.length} dossiers trouvés (${dossierType})`);
            setDossiersList(finalData || []);
        } catch (error) {
            console.error('[DEBUG] ❌ Erreur searchDossiers:', error);
            notify('Erreur lors de la recherche des dossiers', 'error');
        } finally {
            setLoadingDossiers(false);
        }
    };

    // Effet pour recharger les dossiers quand le type change
    useEffect(() => {
        if (!id && dossierType) {
            setSelectedDossier(null);
            setDossierSearchTerm('');
            searchDossiers('');
        }
    }, [dossierType]);

    // Effet pour la recherche debouncée
    useEffect(() => {
        if (!dossierType || id) return;
        
        const timeoutId = setTimeout(() => {
            // On recherche même si la chaîne est vide pour réinitialiser la liste
            searchDossiers(dossierSearchTerm);
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [dossierSearchTerm, dossierType]);

    useEffect(() => {
        if (!id) {
            // On ne pré-charge plus automatiquement le dernier dossier pré-clôturé
            // car cela peut être déroutant pour l'utilisateur.
            // On se contente d'initialiser la liste des dossiers disponibles.
            searchDossiers('');
        } else {
            // Si on vient d'un dossier spécifique
            fetchCircuit(id).then(res => {
                const data = res.data;
                setSelectedDossier(data);
                setDossierType(data.type || initialType);
            });
        }
    }, [id]);

    // Calculs
    const totalPrestations = prestations.reduce((sum, item) => {
        const montantLigne = item.currency === 'EURO' 
            ? Math.round(parseFloat(item.pu) * 655.957 * parseFloat(item.quantite) || 0)
            : Math.round(parseFloat(item.pu) * parseFloat(item.quantite) || 0);
        return sum + montantLigne;
    }, 0);
    const totalReglements = reglements.reduce((sum, item) => {
        const montant = parseFloat(item.montant) || 0;
        return sum + (item.currency === 'EURO' ? Math.round(montant * 655.957) : Math.round(montant));
    }, 0);
    const handleAddPrestation = () => {
        const id = Date.now();
        setPrestations([...prestations, { id, designation: '', nature: '', pu: 0, quantite: 1, montant: 0, currency: 'CFA' }]);
        setLastPrestationId(id);
    };

    const handleRemovePrestation = (id) => {
        if (prestations.length > 1) {
            setPrestations(prestations.filter(p => p.id !== id));
        }
    };

    const handleAddReglement = () => {
        const id = Date.now();
        // Par défaut, on prend la devise de la première prestation si c'est EURO, sinon CFA
        const defaultCurrency = prestations.some(p => p.currency === 'EURO') ? 'EURO' : 'CFA';
        const newReglement = { 
            id, 
            mode: 'Espèces', 
            montant: 0, 
            reference: generateReference('Espèces'), 
            date: dayjs().format('YYYY-MM-DD'),
            currency: defaultCurrency
        };
        setReglements([...reglements, newReglement]);
        setLastReglementId(id);
    };

    const handleRemoveReglement = (id) => {
        if (reglements.length > 1) {
            setReglements(reglements.filter(r => r.id !== id));
        }
    };

    const handleSubmit = () => {
        if (!selectedDossier) {
            notify("Veuillez sélectionner un dossier", "error");
            return;
        }

        // Filter valid entries
        const validPrestations = prestations.filter(p => p.designation && (parseFloat(p.pu) > 0 || parseFloat(p.montant) > 0));
        const validReglements = reglements.filter(r => parseFloat(r.montant) > 0);

        if (validPrestations.length === 0) {
            notify("Veuillez ajouter au moins une prestation valide (montant > 0)", "warning");
            return;
        }

        const data = {
            date: invoiceDate,
            numeroFacture, // UX-13
            dossier: selectedDossier.id,
            typeFacture: typeFacture,
            etat: 'confirmé',
            commentaire,
            adresseClientComptant,
            villeClientComptant,
            paysClientComptant,
            emailClientComptant,
            telephoneClientComptant,
            frais: validPrestations.map(p => {
                const pu = parseFloat(p.pu) || parseFloat(p.montant) || 0;
                // Convertir automatiquement en CFA si la devise est EURO
                const prixUnitaireCFA = p.currency === 'EURO'
                    ? Math.round(pu * 655.957)
                    : Math.round(pu);
                return {
                    designation: p.designation,
                    nature: p.nature || "",
                    quantite: parseFloat(p.quantite) || 1,
                    prixUnitaire: prixUnitaireCFA,
                    currency: p.currency || 'CFA'
                };
            }),
            paiements: reglements
                .filter(r => r.montant > 0)
                .map(r => {
                    const montantPaiement = parseFloat(r.montant);
                    const montantCfa = r.currency === 'EURO' ? Math.round(montantPaiement * 655.957) : Math.round(montantPaiement);
                    return {
                        methode: r.mode,
                        montant: montantCfa,
                        date: r.date,
                        commentaire: r.reference
                    };
                })
        };

        console.log('[DEBUG] Données envoyées au serveur:', JSON.stringify(data, null, 2));
        console.log('[DEBUG] Dossier sélectionné:', selectedDossier);
        console.log('[DEBUG] Prestations valides:', validPrestations);
        console.log('[DEBUG] Règlements valides:', validReglements);

        dispatch(createFactureDossier(data))
            .unwrap()
            .then((res) => {
                notify("Facturation unifiée réussie", "success");
                navigate(`/dossiers/${dossierType}/${selectedDossier.id}/factures/${res.id}`);
            })
            .catch((err) => {
                console.error('[DEBUG] ❌ Erreur creation facture unifiée:', err);
                notify("Erreur lors de la création de la facture", "error");
            });
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, minHeight: '100vh', backgroundColor: '#f0f4f8' }}>
            <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
                
                {/* 🌟 Header Section */}
                <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#102a43', display: 'flex', alignItems: 'center', gap: 1.5, letterSpacing: '-0.5px' }}>
                            <Receipt sx={{ color: COLORS.primary, fontSize: 36 }} />
                            Saisie Facture & Règlement
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#486581', mt: 0.5, maxWidth: 600 }}>
                            Créez la facture et assignez les paiements en une seule étape fluide.
                        </Typography>
                    </Box>
                    <Button 
                        variant="outlined" 
                        startIcon={<ArrowBack />} 
                        onClick={() => navigate(-1)} 
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', borderColor: '#bcccdc', color: '#334e68' }}
                    >
                        Retour
                    </Button>
                </Box>

                {/* 📊 KPI Dashboard (Three structured cards) */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} md={4}>
                        <Paper elevation={0} sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2, borderRadius: '12px', border: '1px solid #e2e8f0', bgcolor: 'white' }}>
                            <Box sx={{ p: 1.5, borderRadius: '10px', bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Add sx={{ color: '#64748b', fontSize: 28 }} />
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Facturé</Typography>
                                <Typography variant="h5" sx={{ color: '#0f172a', fontWeight: 800 }}>{totalPrestations.toLocaleString()} CFA</Typography>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Paper elevation={0} sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2, borderRadius: '12px', border: '1px solid #dcfce7', bgcolor: '#f0fdf4' }}>
                            <Box sx={{ p: 1.5, borderRadius: '10px', bgcolor: '#bbf7d0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <History sx={{ color: '#15803d', fontSize: 28 }} />
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" sx={{ color: '#166534', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Payé</Typography>
                                <Typography variant="h5" sx={{ color: '#14532d', fontWeight: 800 }}>{totalReglements.toLocaleString()} CFA</Typography>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Paper elevation={0} sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2, borderRadius: '12px', border: `1px solid ${totalPrestations - totalReglements > 0 ? '#fee2e2' : '#dcfce7'}`, bgcolor: totalPrestations - totalReglements > 0 ? '#fef2f2' : '#f0fdf4' }}>
                            <Box sx={{ p: 1.5, borderRadius: '10px', bgcolor: totalPrestations - totalReglements > 0 ? '#fecaca' : '#bbf7d0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Receipt sx={{ color: totalPrestations - totalReglements > 0 ? '#b91c1c' : '#15803d', fontSize: 28 }} />
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" sx={{ color: totalPrestations - totalReglements > 0 ? '#991b1b' : '#166534', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Solde Restant</Typography>
                                <Typography variant="h5" sx={{ color: totalPrestations - totalReglements > 0 ? '#7f1d1d' : '#14532d', fontWeight: 800 }}>
                                    {(totalPrestations - totalReglements).toLocaleString()} CFA
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>

                <Grid container spacing={3}>
                    {/* 🗂️ 1. Sélection Dossier */}
                    <Grid item xs={12}>
                        <Paper elevation={2} sx={{ p: 3, borderRadius: '12px', overflow: 'hidden' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: COLORS.primary, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', mr: 2 }}>
                                    1
                                </Box>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#102a43' }}>
                                    Associer à un dossier
                                </Typography>
                            </Box>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={3}>
                                    <TextField
                                        select
                                        fullWidth
                                        size="medium"
                                        label="Type de Prestation"
                                        value={dossierType}
                                        onChange={(e) => setDossierType(e.target.value)}
                                        sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#f8fafc' } }}
                                    >
                                        <MenuItem value="circuit">Circuit</MenuItem>
                                        <MenuItem value="sejour">Séjour</MenuItem>
                                        <MenuItem value="excursion">Excursion</MenuItem>
                                        <MenuItem value="transfert">Transfert</MenuItem>
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Autocomplete
                                        disabled={!!id}
                                        openOnFocus
                                        autoSelect
                                        options={dossiersList}
                                        getOptionLabel={(option) => {
                                            if (typeof option === 'string') return option;
                                            const ref = option.reference || option.numero || 'Inconnu';
                                            const client = option.toClient?.libelle || option.nomClient || option.nom || 'Sans client';
                                            return `${ref} - ${client}`;
                                        }}
                                        value={selectedDossier}
                                        onChange={(e, val) => {
                                            setSelectedDossier(val);
                                            if (val) {
                                                setDossierSearchTerm(val.reference || val.numero || '');
                                            }
                                        }}
                                        inputValue={dossierSearchTerm}
                                        onInputChange={(e, val, reason) => {
                                            if (reason === 'input') {
                                                setDossierSearchTerm(val);
                                            } else if (reason === 'clear') {
                                                setDossierSearchTerm('');
                                                searchDossiers('');
                                            }
                                        }}
                                        loading={loadingDossiers}
                                        isOptionEqualToValue={(option, value) => (option?.id && value?.id ? option.id === value.id : option === value)}
                                        renderOption={(props, option, { index }) => {
                                            const { key, ...otherProps } = props;
                                            const uniqueKey = option.id || option._id || option.reference || option.numero || `dossier-idx-${index}`;
                                            const ref = option.reference || option.numero || 'Dossier';
                                            const client = option.toClient?.libelle || option.nomClient || option.nom || 'Sans client';
                                            return (
                                                <li key={uniqueKey} {...otherProps}>
                                                    <Box>
                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                            {ref} — {client}
                                                        </Typography>
                                                        {option.type && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                Type: {option.type}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </li>
                                            );
                                        }}
                                        renderInput={(params) => (
                                            <TextField 
                                                {...params} 
                                                label="Rechercher (Nom Client, Réf. Dossier...)" 
                                                variant="outlined"
                                                size="medium"
                                                placeholder="Tapez pour rechercher..." 
                                                InputProps={{ ...params.InputProps, startAdornment: (<SearchIcon color="action" sx={{ ml: 1, mr: -0.5 }} />) }}
                                                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#f8fafc' } }}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="N° Facture (Optionnel)"
                                        variant="outlined"
                                        size="medium"
                                        value={numeroFacture}
                                        onChange={(e) => setNumeroFacture(e.target.value)}
                                        placeholder="Saisie libre (ex: FAC-2024-001)"
                                        helperText="Laissez vide pour génération automatique"
                                        sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#f8fafc' } }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        select
                                        label="Type de facture"
                                        variant="outlined"
                                        size="medium"
                                        value={typeFacture}
                                        onChange={(e) => setTypeFacture(e.target.value)}
                                        sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#f8fafc' } }}
                                    >
                                        <MenuItem value="FC">Facture au Comptant</MenuItem>
                                        <MenuItem value="FT">Facture à Terme</MenuItem>
                                        <MenuItem value="AV">Facture d'Avoir</MenuItem>
                                        <MenuItem value="FP">Facture Proforma</MenuItem>
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Date de la facture"
                                        type="date"
                                        variant="outlined"
                                        size="medium"
                                        value={invoiceDate}
                                        onChange={(e) => setInvoiceDate(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#f8fafc' } }}
                                    />
                                </Grid>
                            </Grid>
                            {selectedDossier && (
                                <>
                                    <Box sx={{ mt: 3, mb: 2, p: 2, bgcolor: '#e0f2fe', borderRadius: '8px', borderStyle: 'dashed', borderWidth: '1px', borderColor: '#bae6fd', display: 'flex', gap: 3 }}>
                                        <Typography variant="body2" sx={{ color: '#0369a1', fontWeight: 600 }}>Client: <span style={{ color: '#0c4a6e' }}>{selectedDossier.toClient?.libelle || selectedDossier.nomClient}</span></Typography>
                                        <Typography variant="body2" sx={{ color: '#0369a1', fontWeight: 600 }}>Réf: <span style={{ color: '#0c4a6e' }}>{selectedDossier.reference}</span></Typography>
                                        <Typography variant="body2" sx={{ color: '#0369a1', fontWeight: 600 }}>Date de départ: <span style={{ color: '#0c4a6e' }}>{dateFormatter(selectedDossier.date)}</span></Typography>
                                    </Box>

                                    {/* Si Client Comptant, afficher les champs en libre saisie */}
                                    {(!selectedDossier.toClient && selectedDossier.nomClient) && (
                                        <Box sx={{ mt: 2, p: 2, bgcolor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                            <Typography variant="subtitle2" sx={{ mb: 2, color: '#475569', fontWeight: 'bold' }}>
                                                Informations du client comptant (Libre saisie)
                                            </Typography>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} md={6}>
                                                    <TextField
                                                        fullWidth size="small" variant="outlined"
                                                        label="Adresse"
                                                        value={adresseClientComptant}
                                                        onChange={(e) => setAdresseClientComptant(e.target.value)}
                                                    />
                                                </Grid>
                                                <Grid item xs={6} md={3}>
                                                    <TextField
                                                        fullWidth size="small" variant="outlined"
                                                        label="Ville"
                                                        value={villeClientComptant}
                                                        onChange={(e) => setVilleClientComptant(e.target.value)}
                                                    />
                                                </Grid>
                                                <Grid item xs={6} md={3}>
                                                    <TextField
                                                        fullWidth size="small" variant="outlined"
                                                        label="Pays"
                                                        value={paysClientComptant}
                                                        onChange={(e) => setPaysClientComptant(e.target.value)}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <TextField
                                                        fullWidth size="small" variant="outlined"
                                                        label="Email" type="email"
                                                        value={emailClientComptant}
                                                        onChange={(e) => setEmailClientComptant(e.target.value)}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <TextField
                                                        fullWidth size="small" variant="outlined"
                                                        label="Téléphone / Contact"
                                                        value={telephoneClientComptant}
                                                        onChange={(e) => setTelephoneClientComptant(e.target.value)}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    )}
                                </>
                            )}
                        </Paper>
                    </Grid>

                    {/* 🧾 2. Prestations (Billed Items) */}
                    <Grid item xs={12} md={7}>
                        <Paper elevation={2} sx={{ height: '100%', borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ p: 3, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: COLORS.primary, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', mr: 2 }}>
                                        2
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#102a43' }}>
                                        Lignes de facturation
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#f1f5f9', p: 0.5, borderRadius: '8px' }}>
                                    <Button
                                        size="small"
                                        variant={currency === 'CFA' ? 'contained' : 'text'}
                                        onClick={() => setCurrency('CFA')}
                                        disableElevation
                                        sx={{ textTransform: 'none', borderRadius: '6px', fontWeight: 600, px: 2, color: currency === 'CFA' ? 'white' : '#64748b' }}
                                    >CFA</Button>
                                    <Button
                                        size="small"
                                        variant={currency === 'EURO' ? 'contained' : 'text'}
                                        onClick={() => setCurrency('EURO')}
                                        disableElevation
                                        sx={{ textTransform: 'none', borderRadius: '6px', fontWeight: 600, px: 2, color: currency === 'EURO' ? 'white' : '#64748b' }}
                                    >EURO</Button>
                                </Box>
                            </Box>

                            <Box sx={{ p: 3, flex: 1, bgcolor: '#f8fafc' }}>
                                {prestations.map((p, index) => (
                                    <Paper elevation={0} key={p.id} sx={{ mb: 3, p: 3, border: '1px solid #e2e8f0', borderRadius: '12px', bgcolor: 'white', position: 'relative' }}>
                                        {prestations.length > 1 && (
                                            <IconButton 
                                                size="small" 
                                                sx={{ position: 'absolute', top: 12, right: 12, color: '#ef4444', bgcolor: '#fef2f2', '&:hover': { bgcolor: '#fca5a5', color: 'white' } }} 
                                                onClick={() => handleRemovePrestation(p.id)}
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        )}
                                        <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 2, fontWeight: 700, letterSpacing: '0.5px' }}>LIGNE #{index + 1}</Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={7}>
                                                <TextField
                                                    label="Désignation"
                                                    size="medium" fullWidth placeholder="Ex: Vol Paris-Dakar"
                                                    autoFocus={p.id === lastPrestationId}
                                                    value={p.designation}
                                                    onChange={(e) => {
                                                        const newP = [...prestations];
                                                        newP[index].designation = e.target.value;
                                                        setPrestations(newP);
                                                    }}
                                                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#f8fafc' } }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={5}>
                                                <TextField
                                                    label="Nature / Motif"
                                                    size="medium" fullWidth placeholder="Ex: Billet"
                                                    value={p.nature || ""}
                                                    onChange={(e) => {
                                                        const newP = [...prestations];
                                                        newP[index].nature = e.target.value;
                                                        setPrestations(newP);
                                                    }}
                                                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#f8fafc' } }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <TextField
                                                    label="Prix Unitaire"
                                                    size="medium" type="number" fullWidth
                                                    value={p.pu}
                                                    onChange={(e) => {
                                                        const newP = [...prestations];
                                                        newP[index].pu = e.target.value;
                                                        setPrestations(newP);
                                                    }}
                                                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#f8fafc' } }}
                                                />
                                            </Grid>
                                            <Grid item xs={6} sm={4}>
                                                <TextField
                                                    select label="Devise"
                                                    size="medium" fullWidth
                                                    value={p.currency || 'CFA'}
                                                    onChange={(e) => {
                                                        const newP = [...prestations];
                                                        newP[index].currency = e.target.value;
                                                        setPrestations(newP);
                                                    }}
                                                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#f8fafc' } }}
                                                >
                                                    <MenuItem value="CFA">CFA</MenuItem>
                                                    <MenuItem value="EURO">EURO</MenuItem>
                                                </TextField>
                                            </Grid>
                                            <Grid item xs={6} sm={4}>
                                                <TextField
                                                    label="Quantité"
                                                    size="medium" type="number" fullWidth
                                                    value={p.quantite}
                                                    onChange={(e) => {
                                                        const newP = [...prestations];
                                                        newP[index].quantite = e.target.value;
                                                        setPrestations(newP);
                                                    }}
                                                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#f8fafc' } }}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Box sx={{ mt: 1, p: 2, bgcolor: '#f1f5f9', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>Total pour cette ligne :</Typography>
                                                    <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 800 }}>
                                                        {(p.currency === 'EURO' ? Math.round(p.pu * 655.957 * p.quantite) : Math.round(p.pu * p.quantite)).toLocaleString()} CFA
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                ))}
                                <Button 
                                    startIcon={<Add />} 
                                    onClick={handleAddPrestation} 
                                    size="medium" 
                                    variant="outlined"
                                    fullWidth
                                    sx={{ textTransform: 'none', color: '#0f172a', borderColor: '#cbd5e1', borderRadius: '8px', fontWeight: 600, py: 1.5, borderStyle: 'dashed', borderWidth: 2, '&:hover': { bgcolor: '#f1f5f9', borderColor: '#94a3b8' } }}
                                >
                                    Ajouter une nouvelle ligne de facturation
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* 💸 3. Règlement (Payments) */}
                    <Grid item xs={12} md={5}>
                        <Paper elevation={2} sx={{ height: '100%', borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ p: 3, pb: 2, borderBottom: '1px solid #f1f5f9', bgcolor: '#fffbed' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: '#f59e0b', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', mr: 2 }}>
                                        3
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#102a43' }}>
                                        Règlements associés
                                    </Typography>
                                </Box>
                            </Box>
                            
                            <Box sx={{ p: 3, flex: 1, bgcolor: '#fefce8' }}>
                                {reglements.map((r, index) => (
                                    <Paper elevation={0} key={r.id} sx={{ mb: 2, p: 2.5, border: '1px solid #fde68a', borderRadius: '8px', bgcolor: 'white' }}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <TextField
                                                    select fullWidth size="medium" label="Mode de paiement"
                                                    autoFocus={r.id === lastReglementId}
                                                    value={r.mode}
                                                    onChange={(e) => {
                                                        const newR = [...reglements];
                                                        const newMode = e.target.value;
                                                        newR[index].mode = newMode;
                                                        
                                                        // Générer automatiquement la référence pour Espèces et Mobile Money dès la sélection
                                                        const autoRef = generateReference(newMode);
                                                        if (autoRef) {
                                                            newR[index].reference = autoRef;
                                                        } else if (newR[index].reference && (newR[index].reference.startsWith('ESP-') || newR[index].reference.startsWith('MM-'))) {
                                                            // Vider la référence si on passe d'un mode auto à un mode manuel
                                                            newR[index].reference = '';
                                                        }
                                                        
                                                        setReglements(newR);
                                                    }}
                                                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#f8fafc' } }}
                                                >
                                                    <MenuItem value="Espèces">Espèces</MenuItem>
                                                    <MenuItem value="Chèque">Chèque</MenuItem>
                                                    <MenuItem value="Carte">Carte</MenuItem>
                                                    <MenuItem value="Virement">Virement</MenuItem>
                                                    <MenuItem value="Mobile Money">Mobile Money</MenuItem>
                                                </TextField>
                                            </Grid>
                                            <Grid item xs={6} sm={4}>
                                                <TextField
                                                    label={`Montant réglé (${r.currency || 'CFA'})`} size="medium" fullWidth type="number"
                                                    value={r.montant}
                                                    onChange={(e) => {
                                                        const newR = [...reglements];
                                                        newR[index].montant = e.target.value;
                                                        setReglements(newR);
                                                    }}
                                                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#f8fafc' } }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={2}>
                                                <TextField
                                                    select label="Devise"
                                                    size="medium" fullWidth
                                                    value={r.currency || 'CFA'}
                                                    onChange={(e) => {
                                                        const newR = [...reglements];
                                                        newR[index].currency = e.target.value;
                                                        setReglements(newR);
                                                    }}
                                                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#f8fafc' } }}
                                                >
                                                    <MenuItem value="CFA">CFA</MenuItem>
                                                    <MenuItem value="EURO">EURO</MenuItem>
                                                </TextField>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    label="Référence (N° chèque, N° compte, Id transaction...)" 
                                                    size="medium" fullWidth 
                                                    value={r.reference}
                                                    onChange={(e) => {
                                                        const newR = [...reglements];
                                                        newR[index].reference = e.target.value;
                                                        setReglements(newR);
                                                    }}
                                                    placeholder={r.mode === 'Chèque' ? 'ex: CHQ-001234' : 
                                                                 r.mode === 'Virement' ? 'ex: VIR-2024-001' : 
                                                                 r.mode === 'Mobile Money' ? 'ex: OM-123456' : 
                                                                 r.mode === 'Carte' ? 'ex: CARD-987654' : 
                                                                 generateReference(r.mode) ? `ex: ${generateReference(r.mode)}` : 'Saisir manuellement'}
                                                    helperText={generateReference(r.mode) ? `Référence générée automatiquement: ${generateReference(r.mode)}` : 'Veuillez saisir manuellement la référence'}
                                                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#f8fafc' } }}
                                                />
                                            </Grid>
                                        </Grid>
                                        {reglements.length > 1 && (
                                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                                <Button size="small" color="error" startIcon={<Delete />} onClick={() => handleRemoveReglement(r.id)} sx={{ textTransform: 'none', fontWeight: 600 }}>
                                                    Retirer la ligne
                                                </Button>
                                            </Box>
                                        )}
                                    </Paper>
                                ))}
                                <Button 
                                    startIcon={<Add />} 
                                    onClick={handleAddReglement} 
                                    size="small" 
                                    color="warning"
                                    variant="outlined"
                                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '6px' }}
                                >
                                    Ajouter un paiement
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* 🚀 Footer Action (Save) */}
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                            <Button 
                                variant="contained" 
                                size="large" 
                                startIcon={<Save />} 
                                onClick={handleSubmit}
                                disableElevation
                                sx={{ textTransform: 'none', fontWeight: 700, px: 5, py: 1.5, borderRadius: '8px', bgcolor: COLORS.primary, '&:hover': { bgcolor: '#064566' } }}
                            >
                                Enregistrer Facture & Règlements
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
};

// Helper pour formater la date
const dateFormatter = (date) => {
    if (!date) return '----';
    return dayjs(date).format('DD/MM/YYYY');
};

export default UnifiedFacturationForm;
